import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appPositiveIntegerInput]'
})
export class PositiveIntegerInputDirective {
  @Input() minValue: number = 1;
  @Input() maxValue: number | null = null;
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();

  constructor(private el: ElementRef, @Optional() @Self() private ngControl: NgControl) {}

  private updateModel(value: number): void {
    // Atualiza o modelo de dados do Angular
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(value);
    }
    // Emite o evento customizado
    this.valueChange.emit(value);
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    // Permite: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(charCode) !== -1 ||
        // Permite: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (charCode === 65 && event.ctrlKey === true) ||
        (charCode === 67 && event.ctrlKey === true) ||
        (charCode === 86 && event.ctrlKey === true) ||
        (charCode === 88 && event.ctrlKey === true) ||
        // Permite: home, end, left, right
        (charCode >= 35 && charCode <= 39)) {
      return true;
    }

    // Permite apenas números (0-9)
    if ((charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const value = event.target.value;
    // Remove qualquer caractere que não seja número
    const numericValue = value.replace(/[^0-9]/g, '');

    // Se o valor estiver vazio, permite ficar vazio temporariamente
    if (!numericValue) {
      event.target.value = '';
      return;
    }

    const finalValue = parseInt(numericValue, 10);

    // Aplica validação de limites se necessário
    let validValue = finalValue;
    if (this.maxValue !== null && finalValue > this.maxValue) {
      validValue = this.maxValue;
      event.target.value = this.maxValue.toString();
    } else {
      event.target.value = numericValue;
    }

    // Atualiza o modelo de dados
    this.updateModel(validValue);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');

    // Remove caracteres não numéricos
    const numericValue = pastedText.replace(/[^0-9]/g, '');

    let finalValue: number;
    if (!numericValue) {
      finalValue = this.minValue;
    } else {
      finalValue = parseInt(numericValue, 10);

      // Aplica validação de limites
      if (finalValue < this.minValue) {
        finalValue = this.minValue;
      } else if (this.maxValue !== null && finalValue > this.maxValue) {
        finalValue = this.maxValue;
      }
    }

    // Define o valor no input
    this.el.nativeElement.value = finalValue;

    // Atualiza o modelo de dados
    this.updateModel(finalValue);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    const value = event.target.value;

    // Se o campo estiver vazio ao sair, aplica o valor mínimo
    if (!value || value === '') {
      event.target.value = this.minValue;
      this.updateModel(this.minValue);
      return;
    }

    // Valida se o valor está dentro dos limites ao sair do campo
    const currentValue = parseInt(value, 10);
    let validValue = currentValue;

    if (currentValue < this.minValue) {
      validValue = this.minValue;
      event.target.value = this.minValue;
    } else if (this.maxValue !== null && currentValue > this.maxValue) {
      validValue = this.maxValue;
      event.target.value = this.maxValue;
    }

    // Se o valor foi alterado, atualiza o modelo
    if (validValue !== currentValue) {
      this.updateModel(validValue);
    }
  }
}
